import React, { useState, useRef, useCallback } from 'react';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import useBrand from 'Hooks/useBrand';
import axios from 'axios';
import { toastError } from 'modules/Toast';

// All file types now go to S3 for cost optimization
const SUPPORTED_FILE_TYPES = [
    // Images
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/webp',
    // Videos
    'video/mp4',
    'video/quicktime',
    'video/avi',
    'video/mov',
    // Documents
    'text/csv',
    'application/pdf',
    'application/json',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // Audio
    'audio/mpeg',
    'audio/wav',
    'audio/ogg'
];

export interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: string;
    file: File;
    thumbnailUrl?: string;
    iconType: 'pdf' | 'csv' | 'image' | 'video' | 'audio' | 'document' | 'excel' | 'word';
    uploadProgress: number;
    status: 'pending' | 'uploading' | 'processing' | 'ready' | 'error';
    uploadedAssetId?: number;
    s3Url?: string; // S3 URL for the uploaded file
    errorMessage?: string;
}

interface UniversalUploadWidgetProps {
    onUpload: (files: UploadedFile[]) => void;
    onUploadStart?: (files: UploadedFile[]) => void;
    onUploadProgress?: (fileId: string, progress: number) => void;
    onUploadComplete?: (file: UploadedFile) => void;
    onUploadError?: (fileId: string, error: string) => void;
    buttonId: string;
    buttonRef?: React.RefObject<HTMLButtonElement>;
    maxFiles?: number;
    maxFileSize?: number; // in bytes
    acceptedTypes?: string[];
    allowMultiple?: boolean;
    chatId?: string;
    className?: string;
    externalFiles?: File[]; // For drag & drop integration
}

const UniversalUploadWidget: React.FC<UniversalUploadWidgetProps> = ({
    onUpload,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    onUploadError,
    buttonId,
    buttonRef,
    maxFiles = 10,
    maxFileSize = 100 * 1024 * 1024, // 100MB default
    acceptedTypes = SUPPORTED_FILE_TYPES,
    allowMultiple = true,
    chatId,
    className = '',
    externalFiles
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState<UploadedFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { currentBrand } = useBrand();

    const getFileIconType = (type: string): UploadedFile['iconType'] => {
        if (type.startsWith('image/')) return 'image';
        if (type.startsWith('video/')) return 'video';
        if (type.startsWith('audio/')) return 'audio';
        if (type === 'application/pdf') return 'pdf';
        if (type === 'text/csv' || type === 'application/csv') return 'csv';
        if (type.includes('excel') || type.includes('spreadsheet')) return 'excel';
        if (type.includes('word') || type.includes('document')) return 'word';
        return 'document';
    };

    const generateThumbnail = (file: File): Promise<string | undefined> => {
        return new Promise((resolve) => {
            if (!file.type.startsWith('image/')) {
                resolve(undefined);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    // Thumbnail size
                    const maxSize = 200;
                    let { width, height } = img;
                    
                    if (width > height) {
                        if (width > maxSize) {
                            height = (height * maxSize) / width;
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width = (width * maxSize) / height;
                            height = maxSize;
                        }
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    
                    ctx?.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                };
                img.src = e.target?.result as string;
            };
            reader.readAsDataURL(file);
        });
    };

    const validateFile = (file: File): string | null => {
        if (!acceptedTypes.includes(file.type)) {
            return `File type "${file.type}" is not supported`;
        }
        if (file.size > maxFileSize) {
            return `File size exceeds ${Math.round(maxFileSize / 1024 / 1024)}MB limit`;
        }
        return null;
    };

    const processFiles = async (fileList: FileList) => {
        const files = Array.from(fileList);
        
        if (files.length > maxFiles) {
            alert(`You can only upload ${maxFiles} files at once`);
            return;
        }

        const validFiles: UploadedFile[] = [];
        
        for (const file of files) {
            const error = validateFile(file);
            if (error) {
                alert(`${file.name}: ${error}`);
                continue;
            }

            const thumbnailUrl = await generateThumbnail(file);
            
            const uploadedFile: UploadedFile = {
                id: `${Date.now()}-${Math.random()}`,
                name: file.name,
                size: file.size,
                type: file.type,
                file,
                thumbnailUrl,
                iconType: getFileIconType(file.type),
                uploadProgress: 0,
                status: 'pending'
            };
            
            validFiles.push(uploadedFile);
        }

        if (validFiles.length === 0) return;

        setUploadingFiles(validFiles);
        onUploadStart?.(validFiles);

        // Start uploading files to S3
        await uploadFiles(validFiles);
    };

    const uploadFiles = async (files: UploadedFile[]) => {
        const token = localStorage.getItem('accessToken');
        
        for (const file of files) {
            try {
                // Update status to uploading
                setUploadingFiles(prev => prev.map(f => 
                    f.id === file.id ? { ...f, status: 'uploading' } : f
                ));

                // Upload to S3 via smart upload endpoint
                await uploadToS3(file, token);

            } catch (error) {
                console.error(`Error uploading ${file.name}:`, error);
                const errorMessage = error instanceof Error ? error.message : 'Upload failed';
                
                setUploadingFiles(prev => prev.map(f => 
                    f.id === file.id ? { ...f, status: 'error', errorMessage } : f
                ));
                
                // Show error toast to user
                toastError(`Failed to upload ${file.name}: ${errorMessage}`);
                
                onUploadError?.(file.id, errorMessage);
            }
        }

        // Notify completion
        const completedFiles = uploadingFiles.filter(f => f.status === 'ready');
        onUpload(completedFiles);
    };

    const uploadToS3 = async (file: UploadedFile, token: string | null) => {
        const formData = new FormData();
        formData.append('files', file.file);
        if (chatId) formData.append('chatId', chatId);
        formData.append('generateThumbnails', 'true');

        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/uploaded-assets/smart-upload`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': token ? `Bearer ${token}` : '',
                    'brand-id': currentBrand?.id
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                    setUploadingFiles(prev => prev.map(f => 
                        f.id === file.id ? { ...f, uploadProgress: progress } : f
                    ));
                    onUploadProgress?.(file.id, progress);
                }
            }
        );

        if (response.data.success && response.data.uploads.length > 0) {
            const uploadResult = response.data.uploads[0];
            setUploadingFiles(prev => prev.map(f => 
                f.id === file.id ? { 
                    ...f, 
                    status: 'ready',
                    uploadProgress: 100,
                    uploadedAssetId: uploadResult.id,
                    s3Url: uploadResult.s3Url // Capture the S3 URL from response
                } : f
            ));
            onUploadComplete?.(file);
        } else {
            throw new Error(response.data.error || 'S3 upload failed');
        }
    };

    const handleButtonClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            processFiles(files);
        }
    }, []);

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            processFiles(files);
        }
        // Reset input value to allow re-uploading same file
        e.target.value = '';
    }, []);

    // Add event listener to button
    React.useEffect(() => {
        const button = buttonRef?.current || document.getElementById(buttonId);
        if (button) {
            button.addEventListener('click', handleButtonClick);
            return () => {
                button.removeEventListener('click', handleButtonClick);
            };
        }
    }, [buttonId, buttonRef, handleButtonClick]);

    // Process external files when prop changes (for drag & drop integration)
    React.useEffect(() => {
        if (externalFiles && externalFiles.length > 0) {
            // Convert File[] to FileList-like object
            const fileList = {
                length: externalFiles.length,
                item: (index: number) => externalFiles[index],
                [Symbol.iterator]: function* () {
                    for (let i = 0; i < this.length; i++) {
                        yield this.item(i);
                    }
                }
            } as FileList;
            
            processFiles(fileList);
        }
    }, [externalFiles]);

    return (
        <>
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple={allowMultiple}
                accept={acceptedTypes.join(',')}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />

            {/* Inline Upload Progress - Horizontal Thumbnails */}
            {uploadingFiles.length > 0 && (
                <div 
                    className={`universalUploadInline ${className}`}
                    style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: 0,
                        right: 0,
                        marginBottom: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: `1px solid ${Colors.greyLight}`,
                        borderRadius: 8,
                        padding: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 100
                    }}
                >
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        marginBottom: 8
                    }}>
                        <span style={{ fontWeight: 600, fontSize: 12, color: Colors.textColor }}>
                            Uploading {uploadingFiles.length} file{uploadingFiles.length > 1 ? 's' : ''}
                        </span>
                        <Icon 
                            className="closeIcon"
                            icon="close" 
                            size={14} 
                            color={Colors.greyDark}
                            onClick={() => setUploadingFiles([])}
                            style={{ cursor: 'pointer' }}
                        />
                    </div>

                    {/* Horizontal thumbnail row */}
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        overflowX: 'auto',
                        maxWidth: '100%'
                    }}>
                        {uploadingFiles.map((file) => (
                            <div 
                                key={file.id}
                                style={{
                                    position: 'relative',
                                    minWidth: 64,
                                    width: 64,
                                    height: 64,
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    backgroundColor: Colors.greyLighter,
                                    border: `1px solid ${Colors.grey}`
                                }}
                            >
                                {/* Thumbnail or icon */}
                                {file.thumbnailUrl ? (
                                    <img 
                                        src={file.thumbnailUrl} 
                                        alt={file.name}
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Icon 
                                            className="fileIcon"
                                            icon={file.iconType === 'image' ? 'image' : 
                                                  file.iconType === 'video' ? 'video' :
                                                  file.iconType === 'pdf' ? 'file' :
                                                  file.iconType === 'csv' ? 'file' :
                                                  'file'} 
                                            size={24} 
                                            color={Colors.greyDark}
                                        />
                                    </div>
                                )}

                                {/* Progress overlay */}
                                {file.status === 'uploading' && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: 4,
                                        backgroundColor: 'rgba(0,0,0,0.3)'
                                    }}>
                                        <div style={{
                                            width: `${file.uploadProgress}%`,
                                            height: '100%',
                                            backgroundColor: Colors.purple,
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                )}

                                {/* Progress percentage overlay */}
                                {file.status === 'uploading' && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: 11,
                                        fontWeight: 600
                                    }}>
                                        {file.uploadProgress}%
                                    </div>
                                )}

                                {/* Status overlay */}
                                {file.status === 'ready' && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        backgroundColor: Colors.green,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Icon className="statusIcon" icon="check" size={10} color="white" />
                                    </div>
                                )}

                                {file.status === 'error' && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        backgroundColor: Colors.red,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Icon className="statusIcon" icon="close" size={10} color="white" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Drag & Drop overlay */}
            {isDragOver && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div style={{
                        backgroundColor: 'white',
                        padding: 32,
                        borderRadius: 12,
                        border: `2px dashed ${Colors.blue}`,
                        textAlign: 'center'
                    }}>
                        <Icon className="uploadIcon" icon="upload" size={48} color={Colors.blue} />
                        <div style={{ 
                            marginTop: 16, 
                            fontSize: 18, 
                            fontWeight: 600,
                            color: Colors.textColor
                        }}>
                            Drop files here to upload
                        </div>
                        <div style={{ 
                            marginTop: 8, 
                            fontSize: 14,
                            color: Colors.greyDark
                        }}>
                            Supports images, videos, documents, CSVs, and more
                        </div>
                    </div>
                </div>
            )}

            {/* Add CSS for spin animation */}
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default UniversalUploadWidget; 